/**
 * PATIENT_INFO_LABEL payload (SV-548): the patient's name parts, MRN, raw date
 * of birth and ACTIVE allergies with severity, each allergen and severity named
 * as the screen names them (useEnumTranslation). The print server only formats
 * these ("Name (Severity)" lines, "+N more", DOB, name order) and never reads patient
 * records, so this is the single source for both the label preview and the
 * print request.
 */
import { usePatientsStore, useReferenceStore } from '@/stores';
import type { Patient } from '../types';

type Option = { value: string; label: string };
type AllergenOption = Option;
type SeverityOption = Option & { hl7Code?: string };

export const PATIENT_LABEL_TYPE = 'PATIENT_INFO_LABEL';

/** The screen's translators (useEnumTranslation): allergen names and severity words. */
export interface PatientLabelTranslators {
  /** getTranslatedAllergenName, of the allergen's reference label. */
  allergenName: (name: string) => string;
  /** getAllergenSeverityLabel, of the severity's reference label. */
  severity: (label: string) => string;
}

/** The label payload for a patient (allergies resolved against the reference data). */
export function buildPatientLabelData(
  patient: Pick<Patient, 'firstName' | 'middleName' | 'lastName' | 'medicalRecordNumber' | 'dateOfBirthIso' | 'allergies'>,
  allergenOptions: readonly AllergenOption[],
  severityOptions: readonly SeverityOption[],
  translate: PatientLabelTranslators,
): Record<string, unknown> {
  const allergies = (patient.allergies ?? [])
    .filter((x) => !x.status || x.status.toUpperCase() === 'ACTIVE')
    .map((a) => {
      const allergen = allergenOptions.find((o) => o.value === a.allergy);
      const severity = severityOptions.find((o) => o.value === a.severity);
      return {
        id: a.allergy,
        // Unresolved (reference data missing): the id, visibly wrong rather than silently dropped.
        name: allergen ? translate.allergenName(allergen.label) : a.allergy,
        // hl7 code: ranks the allergy; the label: the word printed, as on the screen.
        severity: severity?.hl7Code ?? null,
        severity_label: severity ? translate.severity(severity.label) : null,
        ...(a.status ? { status: a.status } : {}),
      };
    });
  return {
    first_name: patient.firstName ?? '',
    middle_name: patient.middleName ?? '',
    last_name: patient.lastName ?? '',
    mrn: patient.medicalRecordNumber ?? '',
    dob: patient.dateOfBirthIso ?? '',
    allergies,
  };
}

/**
 * The label payload for a patient, loading what it needs first: the allergen
 * and severity reference data, and the patient's detail record when the given
 * patient came from a list (list rows carry no allergies). When the detail
 * record can't be read, the given patient is used as it is.
 */
export async function loadPatientLabelData(
  patient: Patient,
  translate: PatientLabelTranslators,
): Promise<Record<string, unknown>> {
  const referenceStore = useReferenceStore();
  const [detail] = await Promise.all([
    patient.allergies ? Promise.resolve(patient) : usePatientsStore().getPatient(patient.id, true).catch(() => null),
    referenceStore.loadAllergyReferenceData(),
  ]);
  return buildPatientLabelData(detail ?? patient, referenceStore.allergenOptions, referenceStore.severityOptions, translate);
}
